import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  AlertTriangle, 
  ScanLine, 
  Clock,
  ArrowRight,
  BarChart3,
  Activity,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ExportMenu } from '@/components/ExportMenu'

import { useScanStore } from '@/stores/scanStore'
import { useAuthStore } from '@/stores/authStore'
import { formatRelativeTime, formatRepositoryName } from '@/utils/formatters'
import scanService from '@/services/scans'

interface DashboardStats {
  total_scans: number
  completed_scans: number
  failed_scans: number
  total_findings: number
  critical_findings: number
  high_findings: number
  average_risk_score: number
  scan_trend: Array<{ date: string; count: number }>
  findings_by_severity: Array<{ severity: string; count: number }>
  findings_by_scanner: Array<{ scanner: string; count: number }>
}

const COLORS = {
  CRITICAL: '#dc2626',
  HIGH: '#ea580c',
  MEDIUM: '#d97706',
  LOW: '#2563eb',
  INFO: '#6b7280'
}

export function DashboardPage() {
  const { user } = useAuthStore()
  const { scans, setScans, setLoading } = useScanStore()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [timeRange] = useState('30d')
  
  const recentScans = scans.slice(0, 5)
  const runningScans = scans.filter(scan => scan.status === 'running')

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true)
        
        // Load recent scans and stats in parallel
        const [scansResponse, statsResponse] = await Promise.all([
          scanService.getScans({
            page: 1,
            per_page: 10,
            sort: { field: 'created_at', direction: 'desc' }
          }),
          scanService.getScanStats(timeRange)
        ])
        
        setScans(scansResponse.items)
        setStats(statsResponse)
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadDashboardData()
  }, [timeRange, setScans, setLoading])

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  const formatSeverityData = (data: Array<{ severity: string; count: number }>) => {
    return data.map(item => ({
      name: item.severity,
      value: item.count,
      color: COLORS[item.severity as keyof typeof COLORS] || COLORS.INFO
    }))
  }

  const formatTrendData = (data: Array<{ date: string; count: number }>) => {
    return data.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      scans: item.count
    }))
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
          {getGreeting()}, {user?.full_name || user?.username}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Here's your security overview for today
        </p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Scans</CardTitle>
              <ScanLine className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_scans}</div>
              <p className="text-xs text-muted-foreground">
                {stats.completed_scans} completed, {stats.failed_scans} failed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Findings</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_findings}</div>
              <p className="text-xs text-muted-foreground">
                {stats.critical_findings} critical, {stats.high_findings} high severity
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Risk</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.average_risk_score.toFixed(1)}/10</div>
              <p className="text-xs text-muted-foreground">
                Across all repositories
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Scans</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{runningScans.length}</div>
              <p className="text-xs text-muted-foreground">
                Currently in progress
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Section */}
      {stats && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Scan Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Scan Activity</CardTitle>
              <CardDescription>
                Number of scans performed over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={formatTrendData(stats.scan_trend)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      fontSize={12}
                      tick={{ fill: 'currentColor' }}
                    />
                    <YAxis 
                      fontSize={12}
                      tick={{ fill: 'currentColor' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="scans" 
                      stroke="#2563eb" 
                      strokeWidth={2}
                      dot={{ fill: '#2563eb', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Findings by Severity Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Findings by Severity</CardTitle>
              <CardDescription>
                Distribution of security findings by severity level
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formatSeverityData(stats.findings_by_severity)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {formatSeverityData(stats.findings_by_severity).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center mt-4">
                <div className="flex flex-wrap gap-4">
                  {formatSeverityData(stats.findings_by_severity).map((entry) => (
                    <div key={entry.name} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="text-sm">
                        {entry.name} ({entry.value})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Scans */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Scans</CardTitle>
              <CardDescription>
                Your latest security scan results
              </CardDescription>
            </div>
            <Button variant="ghost" asChild>
              <Link to="/history">
                View All <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentScans.length > 0 ? (
                recentScans.map((scan) => (
                  <div key={scan.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div>
                        {scan.status === 'completed' && <CheckCircle className="h-5 w-5 text-green-600" />}
                        {scan.status === 'failed' && <XCircle className="h-5 w-5 text-red-600" />}
                        {scan.status === 'running' && (
                          <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-blue-600" />
                        )}
                        {scan.status === 'pending' && <Clock className="h-5 w-5 text-yellow-600" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {formatRepositoryName(scan.repository_url)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatRelativeTime(scan.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {scan.summary && (
                        <Badge variant="outline" className="text-xs">
                          {scan.summary.risk_score.toFixed(1)}/10
                        </Badge>
                      )}
                      <Badge
                        variant={
                          scan.status === 'completed' ? 'success' :
                          scan.status === 'failed' ? 'destructive' :
                          scan.status === 'running' ? 'warning' :
                          'secondary'
                        }
                        className="text-xs"
                      >
                        {scan.status}
                      </Badge>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/scan/${scan.id}`}>
                          View
                        </Link>
                      </Button>
                      {scan.status === 'completed' && (
                        <ExportMenu
                          scanId={scan.id}
                          isCompleted={true}
                          variant="button"
                          size="sm"
                        />
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <ScanLine className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No scans yet</p>
                  <Button className="mt-4" asChild>
                    <Link to="/start-scan">Start Your First Scan</Link>
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Common tasks and shortcuts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button className="w-full justify-start" asChild>
              <Link to="/start-scan">
                <ScanLine className="h-4 w-4 mr-2" />
                Start New Scan
              </Link>
            </Button>
            
            <Button variant="outline" className="w-full justify-start" asChild>
              <Link to="/history">
                <BarChart3 className="h-4 w-4 mr-2" />
                View Scan History
              </Link>
            </Button>

            {runningScans.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Active Scans ({runningScans.length})
                </h4>
                <div className="space-y-2">
                  {runningScans.map((scan) => (
                    <div key={scan.id} className="flex items-center justify-between text-sm">
                      <span className="truncate">
                        {formatRepositoryName(scan.repository_url)}
                      </span>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/scan/${scan.id}`}>
                          View
                        </Link>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stats && stats.total_findings > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Critical Issues ({stats.critical_findings})
                </h4>
                <p className="text-xs text-gray-500 mb-2">
                  Immediate attention required
                </p>
                <Button variant="destructive" size="sm" className="w-full">
                  Review Critical Issues
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}